import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import SectionHeader from '../components/SectionHeader';
import { SEO } from '../components/SEO';

// This is a template component used for all service detail pages
export const ServiceDetail = ({ service, pageKey }) => {
  if (!service) return null;

  return (
    <div className="pt-[72px]">
      {pageKey && <SEO page={pageKey} />}
      {/* Hero */}
      <section
        className="relative py-24 text-white"
        style={{
          backgroundImage: `url(${service.heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0" style={{ background: 'rgba(27, 58, 107, 0.55)' }} />
        <div className="relative z-10 max-w-[1280px] mx-auto px-4 sm:px-6">
          <nav className="text-sm mb-6" style={{ color: '#93BBFD' }}>
            <Link to="/" className="hover:text-white">Home</Link>
            <span className="mx-2">&gt;</span>
            <Link to="/services" className="hover:text-white">Services</Link>
            <span className="mx-2">&gt;</span>
            <span className="text-white">{service.title}</span>
          </nav>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">{service.title}</h1>
          <p className="text-xl max-w-3xl">{service.subtitle}</p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6" style={{ color: '#1B3A6B' }}>
              {service.overviewTitle}
            </h2>
            <p className="text-base sm:text-lg" style={{ color: '#1E293B' }}>
              {service.overview}
            </p>
          </div>
        </div>
      </section>

      {/* Services Offered */}
      <section className="py-24" style={{ backgroundColor: '#EFF6FF' }}>
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
          <SectionHeader
            label="WHAT WE OFFER"
            title={service.servicesTitle || `Our ${service.title} Services`}
          />
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {service.services.map((item, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-2xl border border-[#BFDBFE] hover:border-[#1570EF] hover:shadow-xl transition-all duration-300"
              >
                <h3 className="text-xl font-bold mb-4" style={{ color: '#0E7C86' }}>
                  {item.title}
                </h3>
                {item.description && (
                  <p className="text-base mb-4" style={{ color: '#1E293B' }}>
                    {item.description}
                  </p>
                )}
                {item.items && (
                  <ul className="space-y-2">
                    {item.items.map((subItem, subIndex) => (
                      <li key={subIndex} className="flex items-start gap-2 text-sm" style={{ color: '#1E293B' }}>
                        <Check size={18} className="text-[#1570EF] flex-shrink-0 mt-0.5" />
                        <span>{subItem}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Pegasus */}
      {service.whyChoose && (
        <section className="py-24 bg-white">
          <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
            <SectionHeader
              label="WHY CHOOSE US"
              title="Our Differentiators"
            />
            <div className="max-w-4xl mx-auto">
              <ul className="space-y-4">
                {service.whyChoose.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check size={24} className="text-[#1570EF] flex-shrink-0 mt-0.5" />
                    <span className="text-base" style={{ color: '#1E293B' }}>
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 bg-[#1B3A6B] text-white">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-8">
            Ready to Get Started?
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-block px-8 py-4 bg-[#1570EF] text-white font-semibold rounded-full hover:bg-[#1E40AF] hover:scale-105 transition-all duration-200"
            >
              Contact Our Experts
            </Link>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#1B3A6B] font-semibold rounded-full hover:bg-transparent hover:text-white border-2 border-white transition-all duration-200"
            >
              View All Services
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
